<template>
  <main class="rm-page scan-packages-page">
    <header class="rm-topbar rm-topbar--back-only">
      <button
        class="rm-icon-button"
        type="button"
        aria-label="Volver"
        @click="goBack"
      >
        <span aria-hidden="true"></span>
      </button>

      <div class="rm-heading">
        <h1 class="rm-title">Escanear paquetes</h1>
      </div>
    </header>

    <section class="scan-packages-summary" aria-label="Resumen de paquetes">
      <p v-if="packageChipsText" class="rm-chips">{{ packageChipsText }}</p>

      <div class="rm-progress-header">
        <span>Progreso de scaneo</span>
        <span>{{ progressSummary }}</span>
      </div>

      <div
        class="rm-progress rm-progress--md"
        role="progressbar"
        :aria-valuemin="0"
        :aria-valuemax="totalPackages"
        :aria-valuenow="scannedPackages"
        :aria-label="progressSummary"
      >
        <span :style="{ width: progressPercent }"></span>
      </div>
    </section>

    <section class="scan-packages-state-shell" aria-live="polite">
      <div v-if="isLoading" class="scan-packages-state">
        <span class="scan-packages-spinner" aria-hidden="true"></span>
        <p>Cargando paquetes...</p>
      </div>

      <div v-else-if="hasError" class="scan-packages-state">
        <h2>No se pudieron cargar los paquetes</h2>
        <p>{{ errorMessage }}</p>
        <button
          class="rm-button rm-button--outline"
          type="button"
          :disabled="isLoading"
          @click="loadPackages"
        >
          Reintentar
        </button>
      </div>

      <div v-else-if="hasEmptyState" class="scan-packages-state">
        <h2>Sin paquetes asignados</h2>
        <p>{{ emptyMessage }}</p>
        <button
          class="rm-button rm-button--outline"
          type="button"
          :disabled="isLoading"
          @click="loadPackages"
        >
          Recargar
        </button>
      </div>

      <section v-else class="scan-packages-list" aria-label="Paquetes">
        <article
          v-for="packageItem in packages"
          :key="packageItem.key"
          class="scan-package-card"
          :class="{ 'scan-package-card--active': packageItem.isActive }"
          tabindex="0"
          role="button"
          :aria-expanded="packageItem.isActive"
          @click="selectPackage(packageItem)"
          @keydown.enter.prevent="selectPackage(packageItem)"
          @keydown.space.prevent="selectPackage(packageItem)"
        >
          <div class="scan-package-card__main">
            <div>
              <h2>{{ packageItem.task.barcode || 'Paquete sin codigo' }}</h2>
              <p v-if="packageItem.incidentDescription" class="scan-package-card__incident">
                {{ packageItem.incidentDescription }}
              </p>
            </div>

            <span
              class="scan-package-card__status"
              :class="packageStatusClass(packageItem.state)"
            >
              {{ packageStatusText(packageItem.state) }}
            </span>
          </div>

          <div v-if="packageItem.isActive" class="scan-package-card__details">
            <div class="scan-package-card__divider"></div>

            <dl>
              <div>
                <dt>PESO</dt>
                <dd>{{ formatWeight(packageItem.task.weight) || 'No registrado' }}</dd>
              </div>

              <div>
                <dt>MEDIDAS</dt>
                <dd>{{ packageItem.task.measurements || 'No registradas' }}</dd>
              </div>

              <div v-if="packageItem.task.shipmentReference">
                <dt>REFERENCIA ENVIO</dt>
                <dd>{{ packageItem.task.shipmentReference }}</dd>
              </div>

              <div v-if="packageItem.task.note1 || packageItem.task.note2">
                <dt>ENTREGA</dt>
                <dd>{{ packageItem.task.note1 }}</dd>
                <dd v-if="packageItem.task.note2">{{ packageItem.task.note2 }}</dd>
              </div>
            </dl>

            <div
              v-if="packageItem.state === packageScanStates.pending"
              class="scan-package-card__actions"
            >
              <button
                class="rm-button rm-button--warning"
                type="button"
                @click.stop="openIncidentModal(packageItem)"
              >
                Incidencia
              </button>

              <button
                class="rm-button rm-button--outline"
                type="button"
                @click.stop="finalizePackage(packageItem)"
              >
                Finalizado
              </button>
            </div>
          </div>
        </article>
      </section>
    </section>

    <IncidentSelectionModal
      :open="showIncidentSelectionModal"
      :loading="isLoadingIncidentEvents"
      :options="incidentOptions"
      @cancel="closeIncidentModal"
      @select="selectIncidentForActivePackage"
    />

    <PendingPackagesConfirm
      :open="showPendingPackagesConfirm"
      @cancel="cancelContinueWithPending"
      @confirm="confirmContinueWithPending"
    />

    <footer class="scan-packages-footer">
      <div class="scan-packages-counters">
        <div>
          <span>ENTREGADOS</span>
          <strong class="scan-packages-counter--success">{{ deliveredSummary }}</strong>
        </div>
        <div>
          <span>INCIDENCIAS</span>
          <strong class="scan-packages-counter--warning">{{ incidentSummary }}</strong>
        </div>
        <div>
          <span>FALTANTES</span>
          <strong class="scan-packages-counter--primary">{{ missingSummary }}</strong>
        </div>
      </div>

      <button
        class="rm-button rm-button--outline scan-packages-footer__continue"
        type="button"
        :disabled="!hasContentState"
        @click="continueToFinishForm"
      >
        <img
          class="rm-button__icon-img"
          :src="sendIcon"
          alt=""
          aria-hidden="true"
        >
        <span> Continuar</span>
      </button>
    </footer>
  </main>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { mobileRouteStore } from '@/_store/mobileRouteStore';
import sendIcon from '../../../../assets/img/mobile-images/send.svg';
import { loadRouteStopDetail } from '../shared/loadRouteStopDetail';
import IncidentSelectionModal from '../shared/IncidentSelectionModal.vue';
import PendingPackagesConfirm from '../shared/PendingPackagesConfirm.vue';
import {
  buildIncidentOptions,
  buildPackageScanChips,
  buildTaskScanDraft,
  calculatePackageCounters,
  createPackageScanItem,
  formatWeight,
  markPackageFinalized,
  markPackageIncident,
  normalizeStoredIncidentEvents,
  packageScanStates,
  packageStatusClass,
  packageStatusText
} from './scanPackagesModel';

const props = defineProps({
  embedded: {
    type: Boolean,
    default: false
  },
  context: {
    type: Object,
    default() {
      return null;
    }
  }
});
const emit = defineEmits(['navigate', 'back']);
const mobileRoutesStore = mobileRouteStore();
const planId = ref('');
const routeStopId = ref('');
const routeStop = ref(null);
const packages = ref([]);
const isLoading = ref(false);
const errorMessage = ref('');
const emptyMessage = ref('');
const showPendingPackagesConfirm = ref(false);
const showIncidentSelectionModal = ref(false);
const isLoadingIncidentEvents = ref(false);
const activeIncidentPackageKey = ref('');

const counters = computed(() => calculatePackageCounters(packages.value));
const totalPackages = computed(() => counters.value.totalPackages);
const deliveredCount = computed(() => counters.value.deliveredCount);
const incidentCount = computed(() => counters.value.incidentCount);
const missingCount = computed(() => counters.value.missingCount);
const scannedPackages = computed(() => counters.value.scannedPackages);
const hasContentState = computed(() => totalPackages.value > 0);
const hasError = computed(() => !isLoading.value && totalPackages.value === 0 && Boolean(errorMessage.value));
const hasEmptyState = computed(() => !isLoading.value && !hasError.value && totalPackages.value === 0 && Boolean(emptyMessage.value));
const packageChipsText = computed(() => buildPackageScanChips({
  routeStop: routeStop.value,
  packages: packages.value
}).join(' · '));
const progressSummary = computed(() => `${scannedPackages.value} de ${totalPackages.value} completado`);
const progressPercent = computed(() => {
  if (totalPackages.value <= 0) {
    return '0%';
  }

  return `${Math.min(100, Math.max(0, (scannedPackages.value / totalPackages.value) * 100))}%`;
});
const deliveredSummary = computed(() => `${deliveredCount.value}/${totalPackages.value}`);
const incidentSummary = computed(() => `${incidentCount.value}/${totalPackages.value}`);
const missingSummary = computed(() => String(missingCount.value));
const contextIncidentEvents = computed(() => normalizeStoredIncidentEvents(props.context?.incidentEvents || []));
const incidentOptions = computed(() => buildIncidentOptions(
  Array.isArray(mobileRoutesStore.incidentEvents) && mobileRoutesStore.incidentEvents.length
    ? mobileRoutesStore.incidentEvents
    : contextIncidentEvents.value,
  routeStop.value
));

watch(
  () => mobileRoutesStore.routeTasksByStop,
  (routeTasksByStop) => {
    const safeTasks = Array.isArray(routeTasksByStop) ? routeTasksByStop : [];
    packages.value = safeTasks.map(createPackageScanItem);
  }
);

watch(
  () => mobileRoutesStore.incidentEvents,
  () => {
    if (!isLoadingIncidentEvents.value) {
      return;
    }

    isLoadingIncidentEvents.value = false;
  }
);

watch(
  () => mobileRoutesStore.error,
  (error) => {
    if (!isLoadingIncidentEvents.value || !error) {
      return;
    }

    isLoadingIncidentEvents.value = false;
  }
);

onMounted(() => {
  readRouteQuery();
  loadPackages();
});



async function loadPackages() {
  if (isLoading.value) {
    return;
  }

  if (!routeStopId.value) {
    clearPackages();
    errorMessage.value = 'No se recibio la parada en la URL.';
    return;
  }

  isLoading.value = true;
  errorMessage.value = '';
  emptyMessage.value = '';
  packages.value = [];

  try {
    if (planId.value) {
      routeStop.value = await loadRouteStopDetail({
        routeStop: props.context?.routeStop ?? null,
        planId: planId.value,
        routeStopId: routeStopId.value,
        vehicleId: (props.context?.vehicleId || '').toString(),
      });

    }

    await mobileRoutesStore.loadRouteTasksByStop(routeStopId.value);

    if (packages.value.length === 0) {
      emptyMessage.value = 'No hay paquetes asignados para esta parada.';
    }
  } catch (error) {
    if (error.name === 'AbortError') {
      return;
    }

    clearPackages();
    errorMessage.value = error.message || 'No se pudieron cargar los paquetes de la parada.';
  } finally {
    isLoading.value = false;
  }
}

function selectPackage(selectedPackage) {
  packages.value = packages.value.map((packageItem) => ({
    ...packageItem,
    isActive: packageItem.key === selectedPackage.key ? !packageItem.isActive : false,
    isIncidentEditorOpen: packageItem.key === selectedPackage.key
      ? packageItem.isIncidentEditorOpen
      : false
  }));
}

function openIncidentModal(packageItem) {
  packages.value = packages.value.map((currentPackage) => ({
    ...currentPackage,
    isActive: currentPackage.key === packageItem.key
  }));

  activeIncidentPackageKey.value = packageItem.key;
  showIncidentSelectionModal.value = true;
  loadIncidentEvents();
}

function finalizePackage(packageItem) {
  updatePackage(markPackageFinalized(packageItem));
  collapsePackageDetails();
}

function closeIncidentModal() {
  showIncidentSelectionModal.value = false;
  activeIncidentPackageKey.value = '';
}

function selectIncidentForActivePackage(incident) {
  const packageItem = packages.value.find((item) => item.key === activeIncidentPackageKey.value);

  if (!packageItem) {
    closeIncidentModal();
    return;
  }

  updatePackage(markPackageIncident(packageItem, incident));
  collapsePackageDetails();
  closeIncidentModal();
}

function collapsePackageDetails() {
  packages.value = packages.value.map((packageItem) => ({
    ...packageItem,
    isActive: false,
    isIncidentEditorOpen: false
  }));
}

function continueToFinishForm() {
  if (!hasContentState.value) {
    return;
  }

  if (missingCount.value > 0) {
    showPendingPackagesConfirm.value = true;
    return;
  }

  proceedToFinishForm(packages.value);
}

function cancelContinueWithPending() {
  showPendingPackagesConfirm.value = false;
}

function confirmContinueWithPending() {
  const draftPackages = packages.value.map((packageItem) =>
    packageItem.state === packageScanStates.pending
      ? markPackageFinalized(packageItem)
      : packageItem
  );
  packages.value = draftPackages;
  showPendingPackagesConfirm.value = false;
  proceedToFinishForm(draftPackages);
}

function proceedToFinishForm(draftPackages) {
  const draft = buildTaskScanDraft({
    planId: planId.value,
    routeStop: routeStop.value,
    routeStopId: routeStopId.value,
    packages: draftPackages
  });
  if (props.embedded) {
    emit('navigate', {
      view: 'finish-form',
      payload: {
        planId: planId.value,
        routeStopId: routeStopId.value,
        stopId: routeStopId.value,
        routeStop: routeStop.value,
        completionDraft: draft,
        completionMode: 'TaskScan'
      }
    });
    return;
  }

  const target = new URL('/no-scan', window.location.origin);
  target.searchParams.set('planId', planId.value);
  target.searchParams.set('stopId', routeStopId.value);
  target.searchParams.set('mode', 'TaskScan');
  window.location.assign(target.toString());
}

function loadIncidentEvents() {
  const hasStoreEvents = Array.isArray(mobileRoutesStore.incidentEvents)
    && mobileRoutesStore.incidentEvents.length > 0;

  if (isLoadingIncidentEvents.value || hasStoreEvents) {
    return;
  }

  isLoadingIncidentEvents.value = true;
  mobileRoutesStore.loadIncidentEvents();
}

function updatePackage(updatedPackage) {
  packages.value = packages.value.map((packageItem) =>
    packageItem.key === updatedPackage.key
      ? updatedPackage
      : packageItem
  );
}

function goBack() {
  if (props.embedded) {
    emit('back');
    return;
  }

  if (window.history.length > 1) {
    window.history.back();
    return;
  }

  const target = new URL('/service-detail', window.location.origin);
  if (planId.value) {
    target.searchParams.set('planId', planId.value);
  }
  target.searchParams.set('stopId', routeStopId.value);
  window.location.assign(target.toString());
}

function readRouteQuery() {
  if (props.embedded) {
    planId.value = props.context?.planId?.trim?.() ?? '';
    routeStopId.value = (
      props.context?.routeStopId ||
      props.context?.stopId ||
      ''
    ).trim?.() ?? '';
    return;
  }

  const query = new URLSearchParams(window.location.search);
  planId.value = query.get('planId')?.trim() ?? '';
  routeStopId.value = query.get('stopId')?.trim() ?? '';
}

function clearPackages() {
  routeStop.value = null;
  packages.value = [];
  emptyMessage.value = '';
  showPendingPackagesConfirm.value = false;
  showIncidentSelectionModal.value = false;
  activeIncidentPackageKey.value = '';
}
</script>

<style scoped src="./scanPackages.css"></style>
<style scoped src="../shared/routesMobileCommon.css"></style>
